import type { ParsingError } from "../../types";
import type { ConverterResult } from "../types";
import { ConverterErrorType } from "../types";

const MEAN_VALUE_SUFFIX = "VM";
const STD_VALUE_SUFFIX = "EC";
const SUFFIX_SEPARATOR = "_";
const REPLACE_SUFFIX_REGEXP = new RegExp(
  `${SUFFIX_SEPARATOR}${MEAN_VALUE_SUFFIX}|${SUFFIX_SEPARATOR}${STD_VALUE_SUFFIX}`,
  "g",
);

const SKIPPABLE_SENSOR_VALUES = new Set(["INVISIBLE", "VISIBLE"]);

export const JSON_FIELDS = {
  SOURCE_ID: "sourceId",
  ACQUISITION_START_DATE: "acquisitionStartDate",
  ACQUISITION_END_DATE: "acquisitionEndDate",
  LABEL: "label",
  GROUP: "group",
  SENSOR_VALUE_MEAN: "mean",
  SENSOR_VALUE_STD: "std",
  TRANCHE: "tranche",
} as const;

export const CSV_FIELD_NAME_MAPPING = {
  SOURCE: "sourceId",
  STARTDATE: "acquisitionStartDate",
  ENDDATE: "acquisitionEndDate",
  CAMPAGNE: "group",
  CYCLE: "group",
  GROUP: "group",
  HISTORIQUE_ESSAI: "label",
  TEST_LABEL: "label",
  TRANCHE: JSON_FIELDS.TRANCHE,
} as const;

export const CONTEXT_FIELD_NAME_MAPPING = {
  SOURCE: JSON_FIELDS.SOURCE_ID,
  DATEDEBUT: JSON_FIELDS.ACQUISITION_START_DATE,
  DATEFIN: JSON_FIELDS.ACQUISITION_END_DATE,
  CYCLE: JSON_FIELDS.GROUP,
  HISTORIQUE_ESSAI: JSON_FIELDS.LABEL,
  TRANCHE: JSON_FIELDS.TRANCHE,
} as const;

/**
 * Extract lines from csv input
 * Line format will be [key, ...values]
 * Key will be converted matching FIELD_NAME_MAPPING
 * to work with valid json field names
 *
 * @param input csv input string
 * @returns array of lines from given csv input
 */
export function extractLines<Mapping extends Record<string, string>>(
  input: string,
  mapping: Mapping,
) {
  const [, ...lines] = input
    .replace(/\r/gm, "")
    .split("\n")
    .map(row => row.split(";"))
    .map(([key, ...values]): [string, string[]] => [
      typeof key === "string"
        ? Object.hasOwnProperty.call(mapping, key)
          ? mapping[key] || ""
          : key
        : "",
      values,
    ]);
  return lines;
}

/**
 * Extract values for given key
 *
 * @param lines extractLines result
 * @param key JSON key to extract values for
 * @returns Only lines values
 */
export function extractLineValues(lines: [string, string[]][], key: string) {
  return lines.find(line => line[0] === key)?.[1] ?? [];
}

/**
 * Extract sensor values for given column index
 *
 * @param lines extractLines result
 * @param columnIndex Index of current column look up
 * @param date Snapshot date (or acquisitionStartDate) to keep track of possible invalid values.
 * @param loose Mainly for context file, allow to bypass suffix validation (Context file can have any key for mean or std)
 * @returns JSON sensor value format
 */
export function extractSensorValues(
  lines: [string, string[]][],
  columnIndex: number,
  loose: boolean = false,
  date?: string,
) {
  return (
    lines
      /** Keep only sensor related lines */
      .filter(line => {
        const key = line[0];
        const hasMeanSuffix = key?.endsWith(`${SUFFIX_SEPARATOR}${MEAN_VALUE_SUFFIX}`);
        const hasStdSuffix = key?.endsWith(`${SUFFIX_SEPARATOR}${STD_VALUE_SUFFIX}`);
        const hasNoSuffix =
          loose &&
          !hasMeanSuffix &&
          !hasStdSuffix &&
          //   !/_([A-Z]+)/.test(line[0] ?? "") &&
          !(Object.values(CSV_FIELD_NAME_MAPPING) as string[]).includes(key);

        return key.length > 0 && (hasMeanSuffix || hasStdSuffix || hasNoSuffix);
      })
      /** Keep values for current date */
      .map(([sensorTag, values]) => [sensorTag, values[columnIndex]])
      .sort(([a], [b]) =>
        a?.endsWith(MEAN_VALUE_SUFFIX) === b?.endsWith(MEAN_VALUE_SUFFIX)
          ? 0
          : a?.endsWith(MEAN_VALUE_SUFFIX)
            ? -1
            : 1,
      )
      .reduce<{
        sensorValues: ConverterResult["snapshots"][number]["sensorValues"];
        errors: ParsingError<ConverterErrorType>[];
      }>(
        (acc, [sensorTag, value]) => {
          /**
           * We don't accumulate skippable values in the accumulator.
           * Example in context file type, some values are set to INVISIBLE.
           * We silently fail and don't raise errors.
           */
          if (value && SKIPPABLE_SENSOR_VALUES.has(value)) return acc;

          const field =
            // find suffix match mean or std
            sensorTag?.endsWith(`_${STD_VALUE_SUFFIX}`)
              ? JSON_FIELDS.SENSOR_VALUE_STD
              : JSON_FIELDS.SENSOR_VALUE_MEAN;

          const strippedSensorTag = sensorTag?.replace(REPLACE_SUFFIX_REGEXP, "") ?? "";
          const sensorValueIndex = acc.sensorValues.findIndex(
            ({ sensorTag }) => sensorTag === strippedSensorTag,
          );

          /**
           * In the expected format, we only consider string value to infer sensorValue.
           * Otherwise we set it to null.
           */
          const v = typeof value === "string" && value.length > 0 ? parseFloat(value) : null;

          /** Skip empty values */
          if (v === null) return acc;

          /**
           * We don't accumulate invalid values in the accumulator.
           * Instead, we prefer to return an explicit error to keep track of the snapshot.
           */
          if (Number.isNaN(v)) {
            if (loose) return acc;
            acc.errors.push({
              kind: ConverterErrorType.INVALID_SENSOR_VALUE,
              explain: "invalid sensor value",
              location: { file: "", line: 0 },
              data: {
                key: strippedSensorTag,
                value,
                hint: date,
              },
            });
            return acc;
          }

          /**
           * Find existing index or create a new one in accumulator only for mean values,
           * as std values are optionnal and can only be filled with mean values
           */
          if (sensorValueIndex > -1 && acc.sensorValues[sensorValueIndex]) {
            const sensorValue = acc.sensorValues[sensorValueIndex];
            if (sensorValue) sensorValue[field] = v;
          } else {
            if (field === JSON_FIELDS.SENSOR_VALUE_MEAN)
              acc.sensorValues.push({ sensorTag: strippedSensorTag, mean: v });
            if (field === JSON_FIELDS.SENSOR_VALUE_STD) {
              acc.errors.push({
                kind: ConverterErrorType.INVALID_SENSOR_VALUE_MISSING_MEAN,
                explain: "the mean value is missing",
                location: { file: "", line: 0 },
                data: {
                  key: strippedSensorTag,
                  value,
                  hint: date,
                },
              });
            }
          }

          return acc;
        },
        { sensorValues: [], errors: [] },
      )
  );
}

export function assignOptionalFields(
  acquisitionStartDate?: string,
  acquisitionEndDate?: string,
  label?: string,
  group?: string,
) {
  return {
    ...(typeof acquisitionStartDate === "string" ? { acquisitionStartDate } : {}),
    /**
     * If acquisitionEndDate is undefined, we set it to acquisitionStartDate if defined, otherwise we keep it undefined.
     */
    ...(typeof acquisitionEndDate === "string"
      ? { acquisitionEndDate }
      : typeof acquisitionStartDate === "string"
        ? { acquisitionEndDate: acquisitionStartDate }
        : {}),
    ...(typeof label === "string" ? { label } : {}),
    ...(typeof group === "string" ? { group } : {}),
  };
}
